import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAuth } from "../../../../providers/AuthProvider";
import { CategoryBalance, CategoryBalances } from "../../../../types/Dossier";
import { CRMAppLocationIdentifier, FaqProps } from "../../../../types/Faq";
import DossierCategoryTotalRow from "../DossierCategoryTotalRow";

const DossierBalanceTotals = ({
  dossierId,
  balances,
  faqs,
  faqAppLocationIdentifiers,
}: {
  dossierId: string;
  balances: CategoryBalances;
  faqs: FaqProps;
  faqAppLocationIdentifiers: {
    fuel: CRMAppLocationIdentifier;
    servicing: CRMAppLocationIdentifier;
    tyres: CRMAppLocationIdentifier;
    insurance: CRMAppLocationIdentifier;
    rego: CRMAppLocationIdentifier;
    fees: CRMAppLocationIdentifier;
    finance: CRMAppLocationIdentifier;
    total: CRMAppLocationIdentifier;
  };
}) => {
  const { getScopedPageUrl } = useAuth();
  const renderBalanceRow = (
    label: string,
    categoryBalance: CategoryBalance,
    faqAppLocationIdentifier: CRMAppLocationIdentifier,
    targetUrl?: string,
    isTotal: boolean = false
  ) => {
    return (
      <DossierCategoryTotalRow
        label={label}
        values={[
          categoryBalance.budget,
          categoryBalance.expenses,
          categoryBalance.balance,
        ]}
        isTotal={isTotal}
        textSize="small"
        faqs={faqs}
        faqAppLocationIdentifier={faqAppLocationIdentifier}
        decimalPlaces={2}
        targetUrls={[
          `${targetUrl}?includes=invoiced`,
          `${targetUrl}?includes=spent`,
          `${targetUrl}?includes=invoiced,spent`,
        ]}
      />
    );
  };

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "outlineVariant.main",
          py: 0.5,
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
          <Typography variant="titleSmall">Invoiced</Typography>
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
          <Typography variant="titleSmall">Spent</Typography>
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
          <Typography variant="titleSmall">Balance</Typography>
        </Box>
      </Box>
      {renderBalanceRow(
        "Fuel / Charging",
        balances.fuel,
        faqAppLocationIdentifiers.fuel,
        getScopedPageUrl("dossierBudget", dossierId, "fuel")
      )}
      {renderBalanceRow(
        "Service",
        balances.servicing,
        faqAppLocationIdentifiers.servicing,
        getScopedPageUrl("dossierBudget", dossierId, "servicing")
      )}
      {renderBalanceRow(
        "Tyres",
        balances.tyres,
        faqAppLocationIdentifiers.tyres,
        getScopedPageUrl("dossierBudget", dossierId, "tyres")
      )}
      {renderBalanceRow(
        "Insurance",
        balances.insurance,
        faqAppLocationIdentifiers.insurance,
        getScopedPageUrl("dossierBudget", dossierId, "insurance")
      )}
      {renderBalanceRow(
        "Rego / CTP",
        balances.rego,
        faqAppLocationIdentifiers.rego,
        getScopedPageUrl("dossierBudget", dossierId, "rego")
      )}
      {renderBalanceRow(
        "Management Fee",
        balances.fees,
        faqAppLocationIdentifiers.fees,
        getScopedPageUrl("dossierBudget", dossierId, "fees")
      )}
      {renderBalanceRow(
        "Finance",
        balances.finance,
        faqAppLocationIdentifiers.finance,
        getScopedPageUrl("dossierBudget", dossierId, "finance")
      )}
      {renderBalanceRow(
        "Balance",
        balances.total,
        faqAppLocationIdentifiers.total,
        getScopedPageUrl("dossierTransactions", dossierId),
        true
      )}
    </Stack>
  );
};

export default DossierBalanceTotals;
